import React from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Skeleton } from '@mui/material';
import moment from 'moment';
import { useEffectOnce } from "react-use";
import { maxSymbolEllipsis } from "../../../helpers/functions";
import { setProductDetails } from "../../../redux/actions/product_actions";
import chat_bubble_outline from '../../../dist/icons/chat_bubble.svg';
import heart_outline from '../../../dist/icons/heart_outline.svg';
import ProductImagesSlider from "./images_slider";
import classNames from "classnames";

const ProductItem = ({ product, onClick, border = true, showRemoveFav = false, onRemoveFav }) => {
    const dispatch = useDispatch();
    const history = useNavigate();

    const navigateToProductDetailsPage = (product) => {
        dispatch(setProductDetails(product));
        history(`/products/${product.id}`);
    };

    let baseStyle = {
        height: 'auto',
    };

    if (typeof product.features === 'object' && !Array.isArray(product.features)) {
        if (product.features.color !== null) {
            baseStyle.background = product.features.color;
        }
    }

    useEffectOnce(() => {
        moment.locale('ru');
    });

    // Elegant VIP and Urgent borders using Tailwind classes
    const cardBorderClass = classNames(
        "group relative flex flex-col justify-between w-full rounded-2xl bg-white transition-all duration-300 cursor-pointer overflow-hidden",
        {
            "border border-slate-100 hover:border-blue-200 shadow-sm hover:shadow-lg hover:-translate-y-1": border && !product.is_vip && !product.is_urgent,
            "border-2 border-amber-400 shadow-md shadow-amber-50 hover:shadow-xl hover:shadow-amber-100/50 hover:-translate-y-1": border && product.is_vip,
            "border-2 border-rose-500 shadow-md shadow-rose-50 hover:shadow-xl hover:shadow-rose-100/50 hover:-translate-y-1": border && product.is_urgent,
        }
    );

    return (
        <div
            className={cardBorderClass}
            style={baseStyle}
            onClick={() => {
                navigateToProductDetailsPage(product);
            }}
        >
            {/* Elegant Badges */}
            <div className="absolute top-3 left-3 z-10 flex flex-col gap-1.5">
                {product.is_vip && (
                    <span className="px-2.5 py-1 bg-amber-500 text-white text-[10px] font-bold uppercase tracking-wider rounded-lg shadow-sm">
                        VIP
                    </span>
                )}
                {product.is_urgent && (
                    <span className="px-2.5 py-1 bg-rose-600 text-white text-[10px] font-bold uppercase tracking-wider rounded-lg shadow-sm">
                        Срочно
                    </span>
                )}
            </div>

            {showRemoveFav && (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        if (onRemoveFav) onRemoveFav(product.id);
                    }}
                    className="absolute top-3 right-3 z-20 w-8 h-8 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-full border border-slate-100 hover:bg-rose-50 hover:border-rose-200 text-rose-500 hover:text-rose-600 transition-colors shadow-md"
                    title="Удалить из избранного"
                >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                </button>
            )}
            
            {/* Image Section */}
            <div className="relative w-full h-[190px] overflow-hidden bg-slate-50 border-b border-slate-100">
                {product.media?.length ? (
                    <ProductImagesSlider
                        images={[
                            ...product.media.map((item) => item.original_url),
                            product.imageOrDefault
                        ]}
                        className="h-[190px] w-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                ) : product.image ? (
                    <>
                        <img
                            className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                            src={product.image}
                            alt={product.title}
                            id={`prod-img-${product.id}`}
                            onError={(e) => {
                                e.target.style.display = 'none';
                                const fallbackEl = document.getElementById(`prod-fallback-${product.id}`);
                                if (fallbackEl) fallbackEl.classList.remove('hidden');
                            }}
                        />
                        <div id={`prod-fallback-${product.id}`} className="hidden absolute inset-0 flex flex-col items-center justify-center text-slate-400 select-none">
                            <svg className="w-10 h-10 mb-2 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span className="text-xs font-medium text-slate-400">Нет фото</span>
                        </div>
                    </>
                ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 select-none">
                        <svg className="w-10 h-10 mb-2 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-xs font-medium text-slate-400">Нет фото</span>
                    </div>
                )}
            </div>

            {/* Info Section */}
            <div className="flex-1 flex flex-col justify-between p-4 bg-white">
                <div className="space-y-2">
                    <h3 className="text-slate-800 text-[15px] font-semibold line-clamp-1 group-hover:text-blue-600 transition-colors">
                        {product.title}
                    </h3>
                    <p className="text-slate-500 text-xs leading-relaxed min-h-[36px] line-clamp-2">
                        {maxSymbolEllipsis(product.description, 70)}
                    </p>
                    <div className="text-blue-600 text-[16px] font-bold mt-1">
                        {product.priceWithCurrency}
                    </div>
                </div>

                <div className="mt-4 pt-3 border-t border-slate-50 space-y-2">
                    <div className="text-slate-400 text-[11px] font-medium flex items-center">
                        <svg className="w-3.5 h-3.5 mr-1 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        Регион: <span className="text-slate-600 ml-1">{product.city?.name}</span>
                    </div>

                    <div className="flex items-center justify-between text-slate-400 text-[11px]">
                        <span>{moment(product.created_at).fromNow()}</span>

                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1">
                                <img src={chat_bubble_outline} alt="сообщения" className="w-3.5 h-3.5 opacity-60" />
                                <span className="font-semibold text-slate-600">10</span>
                            </div>
                            <div className="flex items-center gap-1">
                                <img src={heart_outline} alt="избранное" className="w-3.5 h-3.5 opacity-60" />
                                <span className="font-semibold text-slate-600">50</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const ProductItemSkeleton = () => {
    return (
        <div className="w-full rounded-2xl border border-slate-100 shadow-sm flex flex-col overflow-hidden bg-white">
            <div className="w-full h-[190px]">
                <Skeleton variant="rectangular" width="100%" height="100%" />
            </div>

            <div className="p-4 space-y-3">
                <Skeleton variant="text" width="60%" height={24} />
                <Skeleton variant="text" width="100%" height={16} />
                <Skeleton variant="text" width="40%" height={20} />

                <div className="pt-3 border-t border-slate-50 flex justify-between items-center">
                    <Skeleton variant="text" width="30%" />
                    <Skeleton variant="text" width="20%" />
                </div>
            </div>
        </div>
    );
};

export default ProductItem;